<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">Add Note</h4>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.addNote" class="form-wrapper">
                <tabs
                    :tab-name="['Notes', 'Photos', 'Documents', 'Preview']"
                    color-button="warning"
                    class="custom-tab-list"
                >
                    <template slot="tab-pane-1">
                        <div>
                            <form-group name="role" class="custom-textarea">
                                <md-textarea v-focus v-model="form.note" @input="update" :md-debounce="100" />
                            </form-group>
                        </div>
                    </template>
                    <template slot="tab-pane-2">
                        <div>
                            <div class="notes-photo-upload">
                                <div
                                    class="photo-dropzone"
                                    :class="{ 'is-dragging': isDraggingPhotos }"
                                    @click="$refs.photoInput.click()"
                                    @dragover.prevent="isDraggingPhotos = true"
                                    @dragleave.prevent="isDraggingPhotos = false"
                                    @drop.prevent="onPhotoDrop"
                                >
                                    <input
                                        ref="photoInput"
                                        type="file"
                                        multiple
                                        accept="image/png,image/gif,image/jpeg,image/jpg,image/bmp,image/webp"
                                        class="photo-input"
                                        @change="onPhotoInputChange"
                                    />
                                    <p v-if="imageList.length === 0" class="dropzone-text">
                                        Drag Images (Multiple)
                                        <br />
                                        <span class="browse-text">(or) Select</span>
                                    </p>
                                    <p v-else class="dropzone-text">
                                        {{ imageList.length }} / {{ maxImageCount }} selected &mdash; click to add more
                                    </p>
                                </div>
                                <div v-if="imageList.length > 0" class="photo-preview-grid">
                                    <div v-for="(img, index) in imageList" :key="index" class="photo-tile">
                                        <img :src="img.photoUrl" alt="" @error="$_setDefaultBrokenImage" />
                                        <button
                                            type="button"
                                            class="photo-remove-btn"
                                            aria-label="Remove image"
                                            @click.stop="removePhoto(index)"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template slot="tab-pane-3">
                        <div>
                            <div>
                                <document-file-upload
                                    :documents="documents"
                                    @upload-success="uploadDocumentSuccess"
                                    @upload-remove="uploadDocumentRemove"
                                />
                            </div>
                        </div>
                    </template>
                    <template slot="tab-pane-4">
                        <div>
                            <div class="add-note-preview" v-html="compiledMarkdown"></div>
                            <div class="photo-preview">
                                <div v-for="(image, index) in imageList" :key="index">
                                    <md-avatar>
                                        <img
                                            :src="image.photoUrl"
                                            class="profile-image"
                                            @error="$_setDefaultBrokenImage"
                                            alt=""
                                        />
                                        <md-tooltip md-direction="top" class="tooltip-image-container">
                                            <div class="tooltip-image">
                                                <img :src="image.photoUrl" @error="$_setDefaultBrokenImage" alt="" />
                                            </div>
                                        </md-tooltip>
                                    </md-avatar>
                                </div>
                            </div>
                        </div>
                    </template>
                </tabs>
                <div class="note-type-container">
                    <div class="type-field">
                        <label class="custom-label">Type</label>
                        <vue-select
                            :reduce="(x) => x.name"
                            label="name"
                            :options="noteTypes"
                            placeholder="Status"
                            v-model="form.type"
                            :searchable="$root.isDesktop"
                            :clearable="false"
                        ></vue-select>
                    </div>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <div class="stats">
                <i class="md-icon md-icon-font md-theme-default">emoji_objects</i>
                <span class="tip">
                    You can also use the
                    <a href="https://github.com/adam-p/markdown-here/wiki/Markdown-Here-Cheatsheet" target="_blank">
                        Markdown syntax
                    </a>
                    to format your notes.
                </span>
            </div>
            <md-button class="dialog-button md-primary" @click="saveNote">
                Save
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">
                Cancel
            </md-button>
        </div>
    </div>
</template>

<script>
import { BlockBlobClient } from '@azure/storage-blob';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { required } from 'vuelidate/lib/validators';
import marked from 'marked';
import { Tabs, DocumentFileUpload } from '@/components';
import { NOTE_TYPE_CONSTANTS, STOP_NOTE_TYPE_CONSTANTS } from '@/utils/constants';

const MAX_IMAGE_COUNT = 10;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/gif', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/webp'];

export default {
    name: 'NotesModal',
    components: { Tabs, DocumentFileUpload },
    mixins: [GeneralMixin],
    props: {
        shipmentId: {
            type: Number,
            default: null
        },
        stopId: {
            type: Number,
            default: null
        },
        tripId: {
            type: Number,
            default: null
        },
        orderId: {
            type: Number,
            default: null
        },
        customerId: {
            type: Number,
            default: null
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            form: {
                note: '',
                type: 'Stop Note'
            },
            selectedPhoto: null,
            photoPreview: null,
            imageList: [],
            imageUrls: [],
            documents: [],
            documentList: [],
            documentUrls: [],
            isDraggingPhotos: false,
            maxImageCount: MAX_IMAGE_COUNT,
            noteTypes: [...NOTE_TYPE_CONSTANTS]
        };
    },
    computed: {
        compiledMarkdown() {
            return marked(this.form.note, { sanitize: true });
        }
    },
    mounted() {
        if (this.shipmentId != null) {
            this.form.type = 'Shipment Note';
        } else if (this.tripId != null) {
            this.form.type = 'Trip Started';
            this.noteTypes = this.noteTypes.concat([...STOP_NOTE_TYPE_CONSTANTS]);
        } else if (this.orderId != null) {
            this.form.type = 'Order Note';
        } else if (this.customerId != null) {
            this.form.type = 'Customer Note';
        } else {
            this.form.type = this.stopId !== null ? 'Stop Note' : 'Other';
            this.noteTypes = this.noteTypes.concat([...STOP_NOTE_TYPE_CONSTANTS]);
        }
    },
    methods: {
        async saveNote() {
            const resp = await this.addNote();
            if (resp.data && (resp.data.photoUploadUrls || resp.data.documentUploadUrls)) {
                const uploadPromiseList = [];
                const uploadDocumentPromiseList = [];
                this.$_handleLoaderState(true, 'SAVING...');

                if (resp.data.photoUploadUrls) {
                    for (let i = 0; i < resp.data.photoUploadUrls.length; i++) {
                        uploadPromiseList.push(
                            this.onUploadImage(resp.data.photoUploadUrls[i], this.imageList[i].file)
                        );
                    }
                    Promise.all(uploadPromiseList).then(() => {
                        this.$_handleLoaderState(false);
                    });
                }

                if (resp.data.documentUploadUrls) {
                    for (let i = 0; i < resp.data.documentUploadUrls.length; i++) {
                        uploadDocumentPromiseList.push(
                            this.onUploadDocument(resp.data.documentUploadUrls[i], this.documentList[i].file)
                        );
                    }
                    Promise.all(uploadDocumentPromiseList).then(() => {
                        this.$_handleLoaderState(false);
                    });
                }
            }
        },
        // eslint-disable-next-line consistent-return
        async addNote() {
            // if (!this.$v.addNote.$invalid) {
            this.$_handleLoaderState(true);
            let api = `/api/stops/${this.stopId}/notes`;
            if (this.shipmentId != null) {
                api = `/api/shipments/${this.shipmentId}/notes`;
            } else if (this.tripId != null) {
                api = `/api/trips/${this.tripId}/notes`;
            } else if (this.orderId != null) {
                api = `/api/orders/${this.orderId}/notes`;
            } else if (this.customerId != null) {
                api = `/api/customers/${this.customerId}/notes`;
            }

            const photoFiles = this.imageList.map((item) => {
                return item.file.name;
            });

            const documentFiles = this.documentList.map((item) => {
                return item.file.name;
            });

            const payload = {
                method: 'post',
                data: {
                    note: this.form.note,
                    type: this.form.type,
                    photoFileNames: photoFiles,
                    DocumentFileNames: documentFiles
                }
            };
            try {
                const response = await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully added note.',
                    type: 'success'
                });
                this.resolve(this.form.note);
                this.$_handleLoaderState(false);
                return response;
            } catch (e) {
                const message = 'Could not add a new note.';
                showErrorMessage(this, message, e);
                this.$_handleLoaderState(false);
                this.resolve(false);
            }
            // }
        },
        update(value) {
            this.form.note = value;
        },
        async onUploadImage(notePhotoUrl, photofile) {
            const blockBlobClient = new BlockBlobClient(notePhotoUrl);

            try {
                await blockBlobClient.upload(photofile, photofile.size, {
                    blobHTTPHeaders: {
                        blobContentType: photofile.type
                    }
                });

                this.selectedPhoto = null;
                this.photoPreview = null;
            } catch (error) {
                this.$notify({
                    message: `Error in uploading photo (${error})`,
                    type: 'danger'
                });
            }
        },
        async onUploadDocument(noteDocumentUrl, documentfile) {
            const blockBlobClient = new BlockBlobClient(noteDocumentUrl);

            try {
                await blockBlobClient.upload(documentfile, documentfile.size, {
                    blobHTTPHeaders: {
                        blobContentType: documentfile.type
                    }
                });

                this.selectedPhoto = null;
                this.photoPreview = null;
            } catch (error) {
                this.$notify({
                    message: `Error in uploading document (${error})`,
                    type: 'danger'
                });
            }
        },
        async uploadDocumentSuccess(filelist) {
            const items = [];
            filelist.forEach((item) => {
                items.push({ photoUrl: URL.createObjectURL(item), file: item, type: item.type });
            });
            this.documents = filelist;
            this.documentList = items;
        },
        uploadDocumentRemove(index, filelist) {
            this.documentList.splice(index, 1);
            this.documents = filelist;
        },
        onPhotoInputChange(event) {
            const files = Array.from(event.target.files || []);
            this.addPhotos(files);
            event.target.value = '';
        },
        onPhotoDrop(event) {
            this.isDraggingPhotos = false;
            const files = Array.from(event.dataTransfer?.files || []);
            this.addPhotos(files);
        },
        addPhotos(files) {
            const validFiles = files.filter((file) => ALLOWED_IMAGE_TYPES.includes(file.type));
            const remainingSlots = this.maxImageCount - this.imageList.length;
            if (remainingSlots <= 0) {
                this.$notify({
                    message: `Maximum of ${this.maxImageCount} images allowed.`,
                    type: 'warning'
                });
                return;
            }
            if (validFiles.length < files.length) {
                this.$notify({
                    message: 'Some files were skipped because they are not supported images.',
                    type: 'warning'
                });
            }
            validFiles.slice(0, remainingSlots).forEach((file) => {
                this.imageList.push({
                    photoUrl: URL.createObjectURL(file),
                    file
                });
            });
        },
        removePhoto(index) {
            const removed = this.imageList[index];
            if (removed && removed.photoUrl) {
                URL.revokeObjectURL(removed.photoUrl);
            }
            this.imageList.splice(index, 1);
        }
    },
    validations: {
        addNote: {
            note: {
                required
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.notes-photo-upload {
    font-family: 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
    width: 100%;
    margin-top: 10px;
}

.photo-dropzone {
    position: relative;
    border: 2px dashed #cfd8dc;
    border-radius: 6px;
    background-color: #f7f9fb;
    padding: 24px 16px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover,
    &.is-dragging {
        border-color: #2b93ff;
        background-color: #eaf4ff;
    }
}

.photo-input {
    display: none;
}

.dropzone-text {
    margin: 0;
    font-size: 14px;
    color: #546e7a;

    .browse-text {
        color: #2b93ff;
        font-weight: 500;
    }
}

.photo-preview-grid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 8px;
}

.photo-tile {
    position: relative;
    height: 110px;
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: hidden;
    background-color: #fafafa;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.photo-remove-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: none;
    background-color: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background-color: rgba(0, 0, 0, 0.75);
    }
}
.add-note-preview {
    text-align: left;
    padding: 20px 16px;
    max-height: 220px;
    overflow: auto;
    border: 1px solid #eee;
    min-height: 220px;
    margin-top: 13px;
    white-space: pre;
}
.note-image {
    height: 100px;
    width: initial;
    margin-right: 10px;
}
.upload-photo-btn {
    margin-bottom: 0px;
}
.notes-image-container {
    margin-top: 10px;
}
.action-btn {
    margin-right: 10px;
}
.action-btns {
    position: absolute;
    right: 20px;
}

.modal-container {
    max-width: 720px;
    // min-height: 500px;
    .modal-body {
        min-height: 400px;
        padding-bottom: 0;
        .form-wrapper {
            position: relative;
            .note-type-container {
                position: absolute;
                right: 0;
                top: -52px;
                z-index: 9;

                .type-field {
                    width: 200px;
                    .custom-label {
                        display: block;
                        margin-bottom: -8px;
                    }
                }
                @media (min-width: 768px) {
                    top: 8px;
                }
            }
        }
    }
    .stats {
        flex-grow: 8;
        .md-icon {
            color: #ffd900;
        }
    }
    .tip {
        font-size: 12px;
    }
}

.custom-tab-list {
    margin-top: -20px;
    margin-bottom: 0 !important;
    ::v-deep .md-card-header {
        padding: 0;
    }

    ::v-deep .md-card-content {
        padding: 0;
        .md-list {
            margin: 0;
            .md-list-item:first-child {
                margin-left: 0;
            }
        }
    }

    .custom-textarea:before,
    .custom-textarea:after {
        border-bottom: none;
    }

    .custom-textarea {
        border-bottom: none;
        textarea {
            border: 1px solid #eee;
            resize: none !important;
            min-height: 288px !important;
        }
    }
}

.photo-preview {
    margin-top: 10px;
    > div {
        height: 45px;
        width: 45px;
        border-radius: 4px;
        margin-right: 5px;
        display: inline-block;
        border: 1px solid #d6d6d6;
        vertical-align: middle;
    }
}

.tooltip-image-container {
    z-index: 9999;
}

.tooltip-image {
    width: 250px;
    display: table-cell;
    text-align: center;
    vertical-align: middle;
    img {
        vertical-align: middle;
    }
}
</style>
